import Big from 'big.js';
import * as buttons from 'smg/buttons';
import { AddCartItems } from 'smg/customer-api/v2/carts';
import { ShowError } from 'smg/errors';
import { ReportValidity } from 'smg/form';
import * as models from 'smg/customer-api/models';
import { IsObject } from 'smg/object';
import * as uploads from 'smg/uploads';
import { DollarFormat } from 'smg/util';
import { Relref } from '../hugo';
import * as pricecatalog from '../pricecatalog';
import * as stockitems from '../stockitems';

main();

function main() {
    stockitems.InitSeeMore();

    const $form = document.querySelector('#stock-item-form') as HTMLFormElement;
    const $fieldset = $form.querySelector('fieldset') as HTMLFieldSetElement;

    const $plating = document.querySelector('#plating') as HTMLSelectElement;
    const $size = document.querySelector('#size') as HTMLSelectElement;
    const $quantity = document.querySelector('#quantity-select') as HTMLSelectElement;
    const $quantityCustomParent = document.querySelector('#quantity-custom-parent') as HTMLElement;
    const $quantityCustom = $quantityCustomParent.querySelector('#quantity-custom') as HTMLInputElement;
    const $attachment = document.querySelector('#attachment') as HTMLSelectElement;

    const $addToCart = document.querySelector('#add-to-cart') as HTMLButtonElement;
    const $priceStartsAt = document.querySelector('.price-starts-at') as HTMLElement;

    const $imgUploader = document.querySelector('#img-uploader') as HTMLElement;
    const $imgUploadsLabel = $imgUploader.querySelector('#img-uploads') as HTMLLabelElement;
    const $imgThumbnailsParent = $imgUploader.querySelector('#thumbnails-parent') as HTMLElement;
    const $imgThumbnails = $imgThumbnailsParent.querySelector('#thumbnails') as HTMLElement;
    const $imgSpinner = $imgThumbnailsParent.querySelector('.spinner-border') as HTMLElement;
    const uploader: uploads.Uploader = new uploads.Uploader($imgUploadsLabel, {
        thumbnails: $imgThumbnails,
        start: () => {
            $imgSpinner.classList.remove('d-none');
            buttons.Start($addToCart);
        },
        stop: () => {
            $imgSpinner.classList.add('d-none');
            buttons.Stop($addToCart);
        },
        thumbnailMaxHeight: '80px',
    });

    interface childLineItem {
        child: string;
        option: string;
        quantity?: number;
    }

    interface getPriceFromOptionsOpts {
        lineItem?: string;
        lineItemType?: string;
        size?: string;
        quantity?: number;
        childLineItems?: childLineItem[];
    }

    document.querySelector('#quantity')?.parentElement?.remove();

    function getPriceFromOptions(opts: getPriceFromOptionsOpts): Big {
        const lineItemName = opts?.lineItem || defaultOpts.lineItem;
        const lineItemTypeName = opts?.lineItemType || defaultOpts.lineItemType;
        const sizeName = opts?.size || defaultOpts.size;
        const quantity = opts?.quantity || defaultOpts.quantity;
        const childLineItems = opts?.childLineItems || defaultOpts.childLineItems;

        if (!lineItemName || !lineItemTypeName || !sizeName || !quantity) {
            throw new Error('Missing required options for price');
        }

        const children = pricecatalog.ChildAmountsOf(lineItemName, Array.isArray(childLineItems) && IsObject(childLineItems[0]) ? childLineItems : []);
        return pricecatalog.UnitPrice({ lineItem: lineItemName, type: lineItemTypeName, size: sizeName, quantity, perUnit: children.perUnit, fixed: children.fixed });
    }

    const children = childLineItemsFromPlatingOption();

    const defaultOpts: getPriceFromOptionsOpts = {
        lineItem: $priceStartsAt.getAttribute('data-line-item') ?? '',
        lineItemType: $priceStartsAt.getAttribute('data-line-item-type') ?? '',
        size: $priceStartsAt.getAttribute('data-size') ?? '',
        quantity: parseInt($priceStartsAt.getAttribute('data-quantity') || '0', 10),
        childLineItems: children,
    };

    $size.value = defaultOpts.size || $size.value;

    // Modified from stockitems initprices function
    function updatePrice(opts?: getPriceFromOptionsOpts) {
        const dollars = DollarFormat(getPriceFromOptions({
            lineItem: opts?.lineItem || defaultOpts.lineItem,
            lineItemType: opts?.lineItemType || defaultOpts.lineItemType,
            size: opts?.size || $size.value || defaultOpts.size,
            quantity: opts?.quantity || parseInt($quantity.value, 10) || parseInt($quantityCustom.value, 10) || defaultOpts.quantity,
            childLineItems: opts?.childLineItems || childLineItemsFromPlatingOption() || defaultOpts.childLineItems,
        }));

        $priceStartsAt.innerText = dollars;

        if (!$priceStartsAt.classList.contains('price-starts-at')) {
            $priceStartsAt.innerText += ' each';
        }

        $priceStartsAt.classList.remove('placeholder-wave');
    }

    updatePrice();

    function updateSizeQtyPrices() {
        const selectedSizeValue = $size.value || defaultOpts.size || '';
        const selectedQuantityValue = $quantity.querySelector<HTMLOptionElement>('option:checked')?.value || defaultOpts.quantity?.toString() || '0';
        $size.innerHTML = '';
        $quantity.innerHTML = '<option value="">Custom</option>';
        const sizes = pricecatalog.Sizes(defaultOpts.lineItem ?? '', defaultOpts.lineItemType ?? '');
        const selectedSize = sizes.find(s => s.names.includes(selectedSizeValue.trim().toLowerCase()));
        if (!selectedSize) {
            throw new Error(`Size ${selectedSizeValue} not found`);
        }
        const selectedSizeLabel = selectedSize.name;
        const currentPrice = getPriceFromOptions({
            lineItem: defaultOpts.lineItem,
            lineItemType: defaultOpts.lineItemType,
            size: selectedSizeLabel,
            quantity: parseInt(selectedQuantityValue, 10),
            childLineItems: childLineItemsFromPlatingOption(),
        });
        for (const size of sizes) {
            const sizeLabel = size.name;
            const sizePrice = getPriceFromOptions({
                lineItem: defaultOpts.lineItem,
                lineItemType: defaultOpts.lineItemType,
                size: sizeLabel,
                quantity: parseInt(selectedQuantityValue, 10),
                childLineItems: childLineItemsFromPlatingOption(),
            });
            if (sizePrice.eq(0)) {
                continue;
            }
            const $option = document.createElement('option');
            $option.value = sizeLabel;
            $option.innerText = sizeLabel;
            const priceDelta = sizePrice.minus(currentPrice);
            if (priceDelta.gt(0)) {
                $option.innerText += ` (+ ${DollarFormat(priceDelta)})`;
            } else if (priceDelta.lt(0)) {
                $option.innerText += ` (- ${DollarFormat(priceDelta.abs())})`;
            }
            $size.appendChild($option);
        }
        for (const price of selectedSize.prices) {
            const $option = document.createElement('option');
            $option.value = price.quantity.toString();
            const sizeQtyPrice = getPriceFromOptions({
                size: selectedSizeLabel,
                quantity: price.quantity,
            });
            $option.innerText = `${price.quantity}`;
            const priceDelta = sizeQtyPrice.minus(currentPrice);
            if (priceDelta.gt(0)) {
                $option.innerText += ` (+ ${DollarFormat(priceDelta)})`;
            } else if (priceDelta.lt(0)) {
                $option.innerText += ` (-${DollarFormat(priceDelta.abs())})`;
            }
            $quantity.appendChild($option);
        }

        $quantity.value = selectedQuantityValue;
        $size.value = selectedSizeValue;
    }

    $size.addEventListener('change', () => {
        updatePrice({ size: $size.value });
    });

    $plating.addEventListener('change', () => {
        updatePrice({ childLineItems: childLineItemsFromPlatingOption() });
    });

    $attachment.addEventListener('change', () => {
        updatePrice({ childLineItems: childLineItemsFromPlatingOption() });
    });

    $quantity.addEventListener('input', () => {
        if (!$quantity.value) {
            $quantityCustomParent.classList.remove('d-none');
            $quantityCustom.value = '100';
            $quantityCustom.required = true;
            $quantity.required = false;
            $quantity.setCustomValidity('');
            updatePrice({ quantity: 100 });
            return;
        } else {
            $quantityCustom.required = false;
            $quantity.required = true;
        }
        let quantity = parseInt($quantity.value, 10);
        if (quantity < 100) {
            quantity = 100;
        }
        updatePrice({ quantity });
    });

    $quantityCustom.addEventListener('input', () => {
        let quantity = parseInt($quantityCustom.value, 10);
        if (quantity < 100) {
            quantity = 100;
        }
        updatePrice({ quantity });
    });

    updateSizeQtyPrices();

    document.querySelectorAll<HTMLOptionElement>('option:has(.price)').forEach($el => {
        let opts: getPriceFromOptionsOpts;
        switch ($el.getAttribute('data-option')) {
        case 'lineItemType':
            opts = {
                lineItemType: $el.value,
            };
            break;
        case 'size':
            opts = {
                size: $el.value,
            };
            break;
        case 'plating':
            opts = {
                childLineItems: [
                    {
                        child: 'Plating',
                        option: $el.value,
                    },
                ],
            };
            break;
        case 'attachment':
            opts = {
                childLineItems: [
                    {
                        child: 'Attachment',
                        option: $el.value,
                    },
                ],
            };
            break;
        default:
            opts = {};
            break;
        }
        const defaultPrice = getPriceFromOptions(defaultOpts);
        const optionPrice = getPriceFromOptions(opts);
        const priceDelta = optionPrice.minus(defaultPrice);
        const $price = $el.querySelector('.price') as HTMLElement;
        if (priceDelta.gt(0)) {
            $price.innerText = ` (+ ${DollarFormat(priceDelta)})`;
        } else if (priceDelta.lt(0)) {
            $price.innerText = ` (- ${DollarFormat(priceDelta.abs())})`;
        }
    });

    function childLineItemsFromPlatingOption() {
        return [
            {
                child: 'Plating',
                option: $plating.value,
            },
            {
                child: 'Attachment',
                option: $attachment.value,
            },
        ] as childLineItem[];
    }

    const imgsByPlating: Record<string, HTMLImageElement[]> = {};
    const $galleryImgs = document.querySelectorAll<HTMLImageElement>('.gallery-image');
    for (const $img of Array.from($galleryImgs)) {
        const platingRaw = $img.getAttribute('data-attributes') || '';
        try {
            const plating = (JSON.parse(platingRaw) as { plating: string }).plating;
            if (!imgsByPlating[plating]) {
                imgsByPlating[plating] = [];
            }
            imgsByPlating[plating].push($img);
        } catch (e) {
            console.error('Error parsing plating data attribute:', e);
        }
    }

    // When the user makes a selection, show an image of that type. The image rotates through the gallery so the same one isn't shown twice in a row.
    $plating.addEventListener('change', () => {
        const plating = $plating.value;
        const $imgs = imgsByPlating[plating] || [];
        const $next = $imgs.shift();
        if ($next) {
            $next.dispatchEvent(new MouseEvent('mouseover'));
            $imgs.push($next);
        }
        updatePrice({ childLineItems: childLineItemsFromPlatingOption() });
    });

    $form.noValidate = true;
    $form.addEventListener('submit', async e => {
        e.preventDefault();

        if (!ReportValidity($form)) {
            return;
        }

        $fieldset.disabled = true;
        buttons.Start($addToCart);

        // Awaited after disable — a PUT-failure throw would otherwise leave the form disabled.
        let allUploads: number[];
        try {
            allUploads = await uploader.getUploadIDs();
        } catch (err) {
            ShowError(err);
            $fieldset.disabled = false;
            buttons.Stop($addToCart);
            return;
        }
        const firstUpload = allUploads.shift();

        AddCartItems({
            uploadID: firstUpload || 0,
            quantity: parseInt($quantity.value, 10),
            cartItemUploadIDs: [...allUploads],
            uploadLineItem: {
                name: 'Soft Enamel Pins (Prepayment)',
                lineItem: 'Lapel Pin',
                type: 'Soft Enamel',
                size: $size.value,
                networkSKUID: models.NetworkSKUID.NETWORK_SK_US_SOFT_ENAMEL_PINS,
                flagIDs: [models.FlagID.FLAG_PRE_DESIGN],
                childLineItems: [
                    ...childLineItemsFromPlatingOption(),
                ],
            },
        }).then(() => {
            window.location.href = Relref('/cart');
        }).catch(err => {
            $fieldset.disabled = false;
            buttons.Stop($addToCart);
            ShowError(err);
        });
    });

    function initGallery() {
        const $stage = document.querySelector('#stage') as HTMLElement;

        const $galleryContainer = document.querySelector<HTMLElement>('#gallery-image-container');
        if (!$galleryContainer) {
            return;
        }
        const $galleryImageParents = $galleryContainer.querySelectorAll<HTMLImageElement>('.gallery-image-parent');

        $galleryImageParents.forEach($parent => {
            const $big = $parent.querySelector<HTMLAnchorElement>('a.big-preview');
            $parent.addEventListener('mouseover', () => {
                if ($big) {
                    $stage.innerHTML = '';
                    $big.classList.remove('d-none');
                    $stage.appendChild($big);
                }
            });
        });

        if ($galleryImageParents.length > 0) {
            $galleryImageParents[0].dispatchEvent(new MouseEvent('mouseover'));
        }

        const $scrollUpBtn = document.querySelector('#scroll-up-btn') as HTMLElement;
        const $scrollDownBtn = document.querySelector('#scroll-down-btn') as HTMLElement;
        const $scrollLeftBtn = document.querySelector('#scroll-left-btn') as HTMLElement;
        const $scrollRightBtn = document.querySelector('#scroll-right-btn') as HTMLElement;

        const scrollBtns = [$scrollUpBtn, $scrollDownBtn, $scrollLeftBtn, $scrollRightBtn];

        for (const btn of scrollBtns) {
            btn.addEventListener('click', e => {
                e.preventDefault();

                switch (btn) {
                case $scrollUpBtn:
                    scrollGallery({ top: -100 }, 'smooth');
                    break;
                case $scrollDownBtn:
                    scrollGallery({ top: 100 }, 'smooth');
                    break;
                case $scrollLeftBtn:
                    scrollGallery({ left: -100 }, 'smooth');
                    break;
                case $scrollRightBtn:
                    scrollGallery({ left: 100 }, 'smooth');
                    break;
                }
            });
        }

        function scrollGallery(opts: ScrollToOptions, behavior?: ScrollBehavior) {
            $galleryContainer?.scrollBy({
                top: opts.top,
                left: opts.left,
                behavior,
            });
        }
    }

    initGallery();
}